import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../Auth/AuthContext';
import { updateSocialPreferences } from '../../utils/socialApi';
import { toast } from '../Toast';

// ============================================================================
// SocialAccountsView — Instagram account connection + preferences.
// Accepts real connection state & handlers from parent. Handle + display name
// are sourced from Instagram (readonly). Timezone + auto-publish persist via
// PATCH /api/social/instagram/preferences and are debounced on change.
// ============================================================================

const TIMEZONE_OPTIONS = [
    { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
    { value: 'America/Denver',      label: 'Mountain Time (US & Canada)' },
    { value: 'America/Chicago',     label: 'Central Time (US & Canada)' },
    { value: 'America/New_York',    label: 'Eastern Time (US & Canada)' },
    { value: 'UTC',                 label: 'UTC' },
    { value: 'Europe/London',       label: 'Europe/London' },
    { value: 'Europe/Berlin',       label: 'Europe/Berlin' },
    { value: 'Asia/Karachi',        label: 'Asia/Karachi' },
    { value: 'Asia/Dubai',          label: 'Asia/Dubai' },
    { value: 'Asia/Tokyo',          label: 'Asia/Tokyo' },
    { value: 'Australia/Sydney',    label: 'Australia/Sydney' },
];

export default function SocialAccountsView({
    instagramConnected = false,
    instagramHandle = '',
    instagramDisplayName = '',
    instagramAvatarUrl = '',
    instagramTimezone = '',
    instagramAutoPublish = true,
    instagramBusy = false,
    onInstagramConnect,
    onInstagramDisconnect,
    onPreferencesSaved,
}) {
    const { tokens } = useAuth();
    const [tz, setTz] = useState(instagramTimezone || 'America/Los_Angeles');
    const [autopost, setAutopost] = useState(instagramAutoPublish);
    const [confirming, setConfirming] = useState(false);
    const [savingPrefs, setSavingPrefs] = useState(false);
    const [avatarBroken, setAvatarBroken] = useState(false);

    // Sync local prefs state when the parent re-fetches (e.g. after connect).
    useEffect(() => { setTz(instagramTimezone || 'America/Los_Angeles'); }, [instagramTimezone]);
    useEffect(() => { setAutopost(instagramAutoPublish); }, [instagramAutoPublish]);
    useEffect(() => { setAvatarBroken(false); }, [instagramAvatarUrl]);

    // Debounced save for preferences. Skips the first run so we don't re-save
    // the freshly-hydrated value and avoid saving while disconnected.
    const mountedRef = useRef(false);
    useEffect(() => {
        if (!instagramConnected || !tokens?.accessToken) return;
        if (!mountedRef.current) { mountedRef.current = true; return; }
        const timeoutId = setTimeout(async () => {
            setSavingPrefs(true);
            const res = await updateSocialPreferences({
                platform: 'instagram',
                timezone: tz,
                autoPublish: autopost,
                accessToken: tokens.accessToken,
            });
            setSavingPrefs(false);
            if (res.ok) {
                if (onPreferencesSaved) onPreferencesSaved({ timezone: res.data?.timezone, autoPublish: res.data?.autoPublish });
            } else {
                toast.error(res.error || 'Failed to save settings');
            }
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [tz, autopost, instagramConnected, tokens?.accessToken]);

    const showAvatar = !!instagramAvatarUrl && !avatarBroken;

    return (
        <div style={{ padding: 30, maxWidth: 680 }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.22em', color: '#a8a29e', marginBottom: 4 }}>Integrations</div>
            <h1 style={{ fontSize: 32, fontWeight: 500, color: '#1f2937', margin: '0 0 4px', letterSpacing: '-0.03em', lineHeight: 1 }}>Accounts</h1>
            <p style={{ fontSize: 13, color: '#78716c', margin: '0 0 28px', lineHeight: 1.5 }}>Connect your Instagram so we can publish on your behalf.</p>

            {/* Account card */}
            {instagramConnected ? (
                <div style={{ borderRadius: 16, background: 'rgba(255,255,255,0.9)', border: '1px solid #e7e5e4', overflow: 'hidden', marginBottom: 24, boxShadow: '0 10px 30px -15px rgba(0,0,0,0.12)' }}>
                    {/* Gradient strip */}
                    <div style={{ height: 6, background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }} />
                    <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{ width: 56, height: 56, borderRadius: 99, padding: 2.5, flexShrink: 0, background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}>
                            <div style={{ width: '100%', height: '100%', borderRadius: 99, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, overflow: 'hidden' }}>
                                {showAvatar ? (
                                    <img
                                        src={instagramAvatarUrl}
                                        alt={instagramHandle ? `@${instagramHandle}` : 'Avatar'}
                                        onError={() => setAvatarBroken(true)}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 99 }}
                                    />
                                ) : (
                                    '📷'
                                )}
                            </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 18, fontWeight: 500, color: '#1f2937', letterSpacing: '-0.02em' }}>@{instagramHandle || 'your.account'}</span>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, color: '#065f46', background: '#d1fae5', borderRadius: 99, padding: '2px 8px' }}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                                    Connected
                                </span>
                            </div>
                            <div style={{ fontSize: 12, color: '#78716c', marginTop: 3 }}>Instagram Business · connected</div>
                        </div>
                        {!confirming ? (
                            <button onClick={() => setConfirming(true)} disabled={instagramBusy} style={{
                                padding: '8px 16px', borderRadius: 10, border: '1px solid #e7e5e4', background: '#fff',
                                fontSize: 13, fontWeight: 500, color: '#57534e', cursor: 'pointer', transition: 'all 0.15s',
                                opacity: instagramBusy ? 0.5 : 1,
                            }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f4'}
                                onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                            >Disconnect</button>
                        ) : (
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button onClick={() => { if (onInstagramDisconnect) onInstagramDisconnect(); setConfirming(false); }} style={{
                                    padding: '8px 14px', borderRadius: 10, border: 'none', background: '#ef4444', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                }}>Yes, disconnect</button>
                                <button onClick={() => setConfirming(false)} style={{
                                    padding: '8px 14px', borderRadius: 10, border: 'none', background: 'transparent', color: '#57534e', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                                }}>Cancel</button>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div style={{ borderRadius: 16, border: '2px dashed #d6d3d1', background: 'rgba(255,255,255,0.5)', padding: '48px 24px', textAlign: 'center', marginBottom: 24 }}>
                    <div style={{
                        width: 64, height: 64, borderRadius: 16, margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', color: '#fff',
                        boxShadow: '0 10px 24px -8px rgba(220,39,67,0.3)',
                    }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                        </svg>
                    </div>
                    <h3 style={{ fontSize: 20, fontWeight: 500, color: '#1f2937', margin: '0 0 4px', letterSpacing: '-0.02em' }}>No account connected</h3>
                    <p style={{ fontSize: 13, color: '#78716c', maxWidth: 360, margin: '0 auto 20px', lineHeight: 1.5 }}>
                        Connect Instagram to start scheduling posts and stories. We'll only ask for the permissions we need.
                    </p>
                    <button onClick={onInstagramConnect} disabled={instagramBusy} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 22px', borderRadius: 99,
                        border: 'none', fontSize: 14, fontWeight: 600, color: '#fff', cursor: 'pointer',
                        background: '#111827', boxShadow: '0 4px 12px -4px rgba(0,0,0,0.3)',
                        transition: 'transform 0.15s', opacity: instagramBusy ? 0.6 : 1,
                    }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.03)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                        {instagramBusy ? (
                           <>
                                <span style={{ display: 'inline-block', width: 16, height: 16, border: '2.5px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'igSpin 0.6s linear infinite' }} />
                                Connecting...
                            </>
                        ) : (
                            <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                                </svg>
                                Continue with Instagram
                            </>
                        )}
                    </button>
                </div>
            )}

            {/* Preferences (only meaningful when connected) */}
            {instagramConnected && (
                <div style={{ borderRadius: 16, background: 'rgba(255,255,255,0.85)', border: '1px solid #e7e5e4', padding: 24, boxShadow: '0 10px 30px -15px rgba(0,0,0,0.12)', position: 'relative' }}>
                    {savingPrefs && (
                        <div style={{ position: 'absolute', top: 14, right: 18, fontSize: 11, color: '#78716c', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ display: 'inline-block', width: 10, height: 10, border: '2px solid rgba(0,0,0,0.15)', borderTopColor: '#111827', borderRadius: '50%', animation: 'igSpin 0.6s linear infinite' }} />
                            Saving…
                        </div>
                    )}
                    <SettingRow label="Handle" sublabel="Your public Instagram handle.">
                        <input value={instagramHandle || ''} readOnly style={{ ...inputStyle, background: '#f9fafb', color: '#6b7280' }} />
                    </SettingRow>
                    <div style={{ borderTop: '1px solid #f5f5f4', margin: '20px 0' }} />
                    <SettingRow label="Display name" sublabel="Pulled from Instagram. Update it on Instagram to change it here.">
                        <input
                            value={instagramDisplayName || ''}
                            readOnly
                            placeholder="—"
                            style={{ ...inputStyle, background: '#f9fafb', color: '#6b7280' }}
                        />
                    </SettingRow>
                    <div style={{ borderTop: '1px solid #f5f5f4', margin: '20px 0' }} />
                    <SettingRow label="Time zone" sublabel="Used for auto-publish scheduling.">
                        <select value={tz} onChange={(e) => setTz(e.target.value)} style={inputStyle}>
                            {TIMEZONE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </SettingRow>
                    <div style={{ borderTop: '1px solid #f5f5f4', margin: '20px 0' }} />
                    <SettingRow label="Auto-publish" sublabel="Publish posts automatically at their scheduled time.">
                        <ToggleSwitch on={autopost} onChange={setAutopost} />
                    </SettingRow>
                </div>
            )}
        </div>
    );
}

function SettingRow({ label, sublabel, children }) {
    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24 }}>
            <div>
                <div style={{ fontSize: 15, fontWeight: 500, color: '#1f2937', letterSpacing: '-0.01em' }}>{label}</div>
                <div style={{ fontSize: 12, color: '#78716c', marginTop: 2, lineHeight: 1.5 }}>{sublabel}</div>
            </div>
            <div>{children}</div>
        </div>
    );
}

function ToggleSwitch({ on, onChange }) {
    return (
        <button onClick={() => onChange(!on)} style={{
            position: 'relative', width: 44, height: 24, borderRadius: 99, border: 'none', cursor: 'pointer',
            background: on ? '#111827' : '#d6d3d1', transition: 'background 0.2s',
        }}>
            <span style={{
                position: 'absolute', top: 2, width: 20, height: 20, borderRadius: 99, background: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.15)', transition: 'left 0.2s',
                left: on ? 22 : 2,
            }} />
        </button>
    );
}

const inputStyle = {
    fontSize: 14, padding: '8px 12px', borderRadius: 10, border: '1px solid #e7e5e4',
    background: '#fff', width: 256, outline: 'none', boxSizing: 'border-box', color: '#1f2937',
};
